import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Headphones, ChevronDown, ChevronRight } from 'lucide-react';
import { useNowPlaying } from '../contexts/NowPlayingContext';
import { ROUTES } from '../config/routes';

/**
 * Sidebar section showing the currently playing book + collapsible chapter list.
 * Always visible (with empty state when nothing is playing).
 */
export function NowPlayingSidebarSection() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    bookId,
    manifest,
    visibleChapters,
    currentChapterIndex,
    seekToChapter,
  } = useNowPlaying();
  const [expanded, setExpanded] = useState(true);

  const isOnRoute = location.pathname === ROUTES.NOW_PLAYING;

  const handleChapterClick = (idx) => {
    seekToChapter(idx);
    navigate(ROUTES.NOW_PLAYING);
  };

  const handleHeaderClick = () => {
    if (bookId && manifest) {
      navigate(ROUTES.NOW_PLAYING);
    }
  };

  return (
    <div className="space-y-1">
      <div
        className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
          isOnRoute
            ? 'bg-primary text-primary-foreground'
            : 'hover:bg-accent hover:text-accent-foreground'
        }`}
      >
        <button
          type="button"
          onClick={handleHeaderClick}
          disabled={!bookId || !manifest}
          className="flex items-center gap-3 flex-1 min-w-0 text-left disabled:cursor-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded"
          aria-label={bookId && manifest ? 'Open now playing reader' : 'Now playing (nothing loaded)'}
        >
          <Headphones className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
          <span className="truncate">Now Playing</span>
        </button>
        {visibleChapters.length > 0 && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
            className="flex-shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded"
            aria-label={expanded ? 'Collapse chapters' : 'Expand chapters'}
            aria-expanded={expanded}
          >
            {expanded ? <ChevronDown className="h-4 w-4" aria-hidden="true" /> : <ChevronRight className="h-4 w-4" aria-hidden="true" />}
          </button>
        )}
      </div>

      {/* Chapter list (collapsible) */}
      {expanded && (
        <div className="ml-4 pl-3 border-l border-border space-y-0.5">
          {!bookId || !manifest ? (
            <p className="px-3 py-2 text-xs text-muted-foreground italic">Nothing playing</p>
          ) : visibleChapters.length === 0 ? (
            <p className="px-3 py-2 text-xs text-muted-foreground italic">No chapters</p>
          ) : (
            visibleChapters.map((ch, i) => (
              <button
                key={i}
                onClick={() => handleChapterClick(i)}
                className={`w-full text-left px-3 py-1.5 text-xs rounded transition-colors truncate ${
                  i === currentChapterIndex
                    ? 'bg-accent text-accent-foreground font-semibold'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                }`}
                title={ch.title}
              >
                {ch.title}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
